import { useQueryClient } from "@tanstack/react-query";

import {
  Input,
  Modal,
  Text,
  toastError,
  toastSuccess,
  useDisclosure,
} from "@alpacahq/ui/old";

import { useGetClock, useGetMargin, useMutationRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import {
  type Account,
  type ExcerciseOptionParams,
  exerciseOption,
  type Position,
  type Product,
} from "~/v2/api/rest/account";
import type { OptionContract } from "~/v2/api/rest/data";
import { getPrettyAmount, getPrettyCash } from "~/v2/utils/formatting";
import { CountdownButton } from "../CountdownButton";

interface ContractDetailProps {
  product?: Product;
  position: Position;
  underlyingPositions: Position[];
  account: Account;
  contract: OptionContract | undefined;
}

export const ExerciseContract = ({
  product,
  position,
  underlyingPositions,
  account,
  contract,
}: ContractDetailProps) => {
  const client = useQueryClient();

  const { isOpen, onClose, onOpen } = useDisclosure();

  // Assuming useGetMargin is a hook to get margin including available cash
  const { margin } = useGetMargin({
    accountID: account?.id || "",
    paper: product === "paper",
  });

  // To show the note about limit orders being queued when the market is closed
  const { clock } = useGetClock();

  // Get available cash and shares to exercise
  const availableCash = parseFloat(margin?.cash || "0");
  const sharesToExercise = parseInt(contract?.size || "0", 10);
  const strikePrice = parseFloat(contract?.strike_price || "0");
  const isCall = contract?.type === "call";

  // Calculate total cost/proceeds of exercising
  // For calls: you pay (strike price * shares) to buy shares
  // For puts: you receive (strike price * shares) from selling shares
  const totalAmount = strikePrice * sharesToExercise;

  // Check if user can exercise
  // For calls: need enough cash to buy shares
  // For puts: need to have available shares of the base asset to sell (not tied up in orders)
  const availableShares = underlyingPositions.reduce(
    (acc, cur) => acc + Number(cur.qty_available ?? "0"),
    0,
  );
  const canExercise = isCall
    ? availableCash >= totalAmount
    : availableShares >= sharesToExercise;

  const mutation = useMutationRequest<unknown, ExcerciseOptionParams>(
    exerciseOption,
    {
      onSuccess: () => {
        toastSuccess("Position exercise request submitted");

        client.invalidateQueries({ queryKey: queryKeys.orders.all });
        client.invalidateQueries({ queryKey: queryKeys.positions.all });
        client.invalidateQueries({ queryKey: queryKeys.balances.all });
        client.invalidateQueries({ queryKey: queryKeys.options.all });
        onClose();
      },
      onError: (error) => {
        toastError(`Error exercising position: ${error.message}`);
      },
    },
  );

  const onConfirm = async () => {
    if (!account?.id || !contract?.symbol) {
      return;
    }
    mutation.mutate({
      product: product,
      accountID: account.id,
      symbol: contract.symbol,
    });
  };
  return (
    <>
      {!!position && (
        <button
          type="button"
          onClick={onOpen}
          // @todo This is just a temporary styling (we're moving fast OK?)
          className="flex h-10 cursor-pointer items-center justify-center rounded-lg bg-zinc-100 px-4 font-medium text-sm transition-all duration-400 ease-in-out hover:bg-zinc-200"
        >
          Exercise
        </button>
      )}
      <Modal
        title="Exercise"
        isOpen={isOpen}
        onClose={onClose}
        controls={
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={onClose}
              // @todo This is just a temporary styling (we're moving fast OK?)
              className="flex h-10 cursor-pointer items-center justify-center rounded-lg bg-zinc-100 px-4 font-medium text-sm transition-all duration-400 ease-in-out hover:bg-zinc-200"
            >
              Back
            </button>
            <CountdownButton onConfirm={onConfirm} />
          </div>
        }
      >
        <div className="flex flex-col gap-8">
          <div>
            <Input
              id="strikePrice"
              label="Strike"
              value={getPrettyCash(contract?.strike_price)}
              disabled
            />
          </div>
          <div>
            <div className="flex justify-between">
              <span className="text-sm text-zinc-500">
                {isCall
                  ? `${getPrettyCash(availableCash)} cash available`
                  : `${availableShares} shares available`}
              </span>
            </div>
            <Input
              id="totalAmount"
              label={isCall ? "Total Cost" : "Total Proceeds"}
              value={getPrettyCash(totalAmount)}
              disabled
            />
            {!canExercise && (
              <div className="mt-2 text-rose-500 text-sm">
                {isCall
                  ? "Insufficient funds available."
                  : "Insufficient shares available."}
              </div>
            )}
          </div>
          <Text>
            <span className="mr-auto mb-2 block w-fit text-left font-medium text-base">
              Summary
            </span>
            You're exercising a contract to {isCall ? "buy" : "sell"}{" "}
            {getPrettyAmount(sharesToExercise, 0)} shares of{" "}
            <span className="inline-block w-fit bg-blue-100 px-0.5 text-blue-600 text-sm">
              {contract?.symbol}
            </span>{" "}
            {isCall ? "for" : "at"} ${contract?.strike_price} each. Please note
            that you won't be able to cancel this exercise once submitted.
            {!clock?.is_open && (
              <span className="mt-2 text-zinc-500">
                Note: Your limit order will be queued for execution at the
                beginning of the upcoming trading session. It will remain active
                until 4:00 PM tomorrow unless it gets executed before that time.
              </span>
            )}
          </Text>
        </div>
      </Modal>
    </>
  );
};
